import StyledSubheading from '@/components/StyledSubheading/StyledSubheading.vue';
import TitlePanel from '@/components/TitlePanel/TitlePanel.vue';
import '@/scss/main.scss';
import '@/scss/tailwind.css';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faRectangleList as faRectangleListClassic, faEye } from '@fortawesome/free-regular-svg-icons';
import {
  faArrowLeftLong,
  faArrowRightFromBracket,
  faArrowUpRightFromSquare,
  faBan,
  faBars,
  faBug,
  faBugSlash,
  faCheck,
  faChevronDown,
  faChevronRight,
  faCircleInfo,
  faCopy,
  faDownload,
  faFile,
  faFileArrowDown,
  faFolderOpen,
  faGlobe,
  faHouse,
  faMagnifyingGlass,
  faPen,
  faPencil,
  faPenToSquare,
  faPlus,
  faRectangleList,
  faRightFromBracket,
  faToolbox,
  faTrash,
  faTrashCan,
  faUpload,
  faUser,
  faUsers,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { faFileCad } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import VueMarkdownEditor from '@kangc/v-md-editor';
import deDE from '@kangc/v-md-editor/lib/lang/de-DE';
import '@kangc/v-md-editor/lib/style/base-editor.css';
import '@kangc/v-md-editor/lib/theme/style/vuepress.css';
import vuepressTheme from '@kangc/v-md-editor/lib/theme/vuepress.js';
import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';
import { compact } from 'lodash';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import { createApp } from 'vue';
import { createI18n } from 'vue-i18n';
import { createRouter, createWebHashHistory } from 'vue-router';
import { z, ZodInvalidTypeIssue } from 'zod';
import App from './App.vue';
import VfButton from './components/VfButton/VfButton.vue';
import messages from './localization/root';
import globalUtils from './mixins/globalUtils';
import { getLocale, PrimeVuePreset } from './primeVue';
import routes from './routes';
import './scss/vfDds.css';
import store from './store';

library.add(
  faHouse,
  faBars,
  faFolderOpen,
  faUsers,
  faUser,
  faRightFromBracket,
  faChevronRight,
  faChevronDown,
  faPlus,
  faCircleInfo,
  faArrowLeftLong,
  faToolbox,
  faMagnifyingGlass,
  faPencil,
  faPenToSquare,
  faUpload,
  faTrash,
  faTrashCan,
  faCheck,
  faXmark,
  faFileArrowDown,
  faFile,
  faArrowUpRightFromSquare,
  faDownload,
  faPen,
  faRectangleList,
  faRectangleListClassic,
  faEye,
  faArrowRightFromBracket,
  faCopy,
  faGlobe,
  faFileCad,
  faBug,
  faBugSlash,
  faBan,
);

export const i18n = createI18n<[typeof messages.de], 'en' | 'de'>({
  /** Temporary */
  legacy: true,
  /** dynamically set in App.vue mounted fn */
  locale: 'de',
  messages,
});

function initializeZod() {
  z.setErrorMap((issue, ctx) => {
    const i18nParameters = { ...issue, ...ctx };
    if ((issue as ZodInvalidTypeIssue).expected) {
      (i18nParameters as any).expected = i18n.global.t(
        `validations.types.${(issue as ZodInvalidTypeIssue).expected}`,
      );
    }
    return {
      message: i18n.global.t(
        compact([
          'validations',
          issue.code,
          'type' in issue && issue.type,
        ]).join('.'),
        i18nParameters,
      ),
    };
  });
}

export function bootstrapApp() {
  const app = createApp(App);

  const router = createRouter({
    history: createWebHashHistory(),
    routes,
    scrollBehavior() {
      return { top: 0, left: 0 };
    },
  });

  app.component('FontAwesomeIcon', FontAwesomeIcon);
  app.component('VfButton', VfButton);
  app.component('StyledSubheading', StyledSubheading);
  app.component('TitlePanel', TitlePanel);
  app.directive('tooltip', Tooltip);

  app.use(PrimeVue, {
    locale: getLocale(),
    theme: {
      preset: PrimeVuePreset,
      options: {
        darkModeSelector: false,
      },
    },
  });
  app.use(ToastService);

  initializeZod();
  app.use(i18n);

  VueMarkdownEditor.use(vuepressTheme);
  /** dynamically set in App.vue mounted fn */
  VueMarkdownEditor.lang.use('de-DE', deDE);

  app.mixin(globalUtils);

  app.use(store);
  app.use(router);

  router.beforeEach((to, from, next) => {
    if (!to.meta.projectContext) {
      store.commit('clearCurrentProject');
    } else if (to.params.projectId) {
      store.commit('setCurrentProject', to.params.projectId);
    } else if (to.params.id) {
      store.commit('setCurrentProject', to.params.id);
    } else if (to.query.projectId) {
      store.commit('setCurrentProject', to.query.projectId);
    } else if (to.query.id) {
      store.commit('setCurrentProject', to.query.id);
    }
    next();
  });

  app.use(VueMarkdownEditor);
  app.use(FloatingVue);

  app.mount('#app');
}
